import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Compteur } from '../Compteur'
import { Modale } from '../Modale'
import { fcfa } from '../Format'
import {
  NOM_FOURNISSEUR,
  REGLAGES_PAR_DEFAUT,
  ecrireReglages,
  enregistrerAjustement,
  estVerse,
  fixerPlafondMarchand,
  forcerInclusion,
  libelleFrais,
  listerLots,
  listerSoldes,
  lireReglages,
  marquerLotVerse,
  marquerToutVerse,
  messageErreur,
  preparerReversements,
  releveDuLot,
  resumeEnAttente,
  trierLots,
  validerReglages,
  type LigneReleve,
  type LotReversement,
  type ReglagesReversement,
  type SoldeMarchand,
} from '../../api/reversements'

/**
 * Reversements des marchands — le SOIR MÊME de la livraison.
 *
 * Un reversement = un marchand × un jour : toutes les commandes livrées dans la journée y sont regroupées. Le dispatch
 * fait les virements à la main (aucune API Wave / Orange branchée), puis marque « versé » : en un clic pour toute la
 * liste, ou marchand par marchand avec la référence du virement.
 *
 * ⚠️ « Marquer versé » n'EXÉCUTE aucun virement : cela enregistre qu'il a été fait. Le serveur refuse de marquer un
 * montant qui a changé depuis l'affichage, et un lot déjà marqué par un collègue.
 */

const jourLisible = (d: string) => {
  try {
    return new Date(`${d}T00:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' })
  } catch {
    return d
  }
}
const heureLisible = (iso: string | null) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
  } catch {
    return iso
  }
}
const compteAffiche = (fournisseur: string | null, tel: string | null) =>
  tel ? `${NOM_FOURNISSEUR[fournisseur ?? ''] ?? fournisseur ?? ''} ${tel}`.trim() : 'Compte non renseigné'

export function EcranReversements({ estAdmin }: { estAdmin: boolean }) {
  const [lots, setLots] = useState<LotReversement[]>([])
  const [soldes, setSoldes] = useState<SoldeMarchand[]>([])
  const [reglages, setReglages] = useState<ReglagesReversement | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const [chargement, setChargement] = useState(true)
  const [migrationAbsente, setMigrationAbsente] = useState(false)
  const [lotAConfirmer, setLotAConfirmer] = useState<LotReversement | null>(null)
  const [toutOuvert, setToutOuvert] = useState(false)
  const [detail, setDetail] = useState<string | null>(null)
  const [releves, setReleves] = useState<Record<string, LigneReleve[] | 'chargement' | 'erreur'>>({})

  const charger = useCallback(async () => {
    try {
      const [l, s, r] = await Promise.all([listerLots(), listerSoldes().catch(() => [] as SoldeMarchand[]), lireReglages().catch(() => null)])
      setLots(l)
      setSoldes(s)
      setReglages(r)
      setMigrationAbsente(r === null)
      setErreur(null)
    } catch (e) {
      setErreur(messageErreur(e, 'Lecture impossible.'))
    } finally {
      setChargement(false)
    }
  }, [])

  /* À l'ouverture : on prépare les lots du jour (idempotent — rejouer ne change rien), puis on lit. */
  useEffect(() => {
    let vivant = true
    void (async () => {
      try {
        await preparerReversements()
      } catch (e) {
        if (vivant) {
          const m = messageErreur(e, '')
          // Fonction absente = migration 0065 pas encore appliquée : l'écran le dit au lieu d'afficher un code.
          if (/generer_reversements_marchands|PGRST202|schema cache/i.test(m)) setMigrationAbsente(true)
          else if (m) setErreur(m)
        }
      }
      if (vivant) await charger()
    })()
    return () => {
      vivant = false
    }
  }, [charger])

  const triés = useMemo(() => trierLots(lots), [lots])
  const enAttente = triés.filter((l) => !estVerse(l))
  const verses = triés.filter(estVerse).slice(0, 60)
  const resume = resumeEnAttente(lots)
  const aRevoir = soldes.filter((s) => s.aRevoir)

  const agir = async (f: () => Promise<void>, message?: string) => {
    setOccupe(true)
    setErreur(null)
    setInfo(null)
    try {
      await f()
      if (message) setInfo(message)
    } catch (e) {
      setErreur(messageErreur(e))
    } finally {
      await charger()
      setOccupe(false)
    }
  }

  async function ouvrirReleve(id: string) {
    if (detail === id) {
      setDetail(null)
      return
    }
    setDetail(id)
    if (releves[id] && releves[id] !== 'erreur') return
    setReleves((r) => ({ ...r, [id]: 'chargement' }))
    try {
      const lignes = await releveDuLot(id)
      setReleves((r) => ({ ...r, [id]: lignes }))
    } catch {
      setReleves((r) => ({ ...r, [id]: 'erreur' }))
    }
  }

  if (chargement) return <p style={{ padding: 20, color: 'var(--n400)' }}>Chargement…</p>

  if (migrationAbsente && lots.length === 0)
    return (
      <div className="card">
        <div className="card-header">Reversements marchands</div>
        <div className="card-body" role="status">
          Le reversement quotidien n’est pas encore activé sur cette base (migration 0065 à appliquer). L’ancien écran « Paie
          marchands » ne fonctionne plus depuis ce changement : appliquez la migration, puis rechargez.
        </div>
      </div>
    )

  const heure = reglages?.heureLimite ?? REGLAGES_PAR_DEFAUT.heureLimite

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">Reversements marchands — reversés le soir même de la livraison</div>
        <div className="card-body">
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--n700)', lineHeight: 1.55 }}>
            Toutes les commandes livrées dans la journée (jusqu’à {heure.replace(':', ' h ')}, heure de Bamako) sont regroupées en un seul
            reversement par marchand : ses ventes moins notre commission, moins les retenues éventuelles. Faites les virements
            Orange Money / Wave, puis marquez-les « versés ». <b>Marquer versé n’envoie pas d’argent</b> : cela enregistre que le
            virement a été fait.
            {reglages?.payoutMode === 'par_commande' && (
              <> <b>Mode actuel : un reversement par commande.</b></>
            )}
          </p>

          <div className="stats" style={{ marginBottom: 6 }}>
            <div className="stat">
              <div className="label">À virer maintenant</div>
              <div className="val"><Compteur valeur={fcfa(resume.aVirer)} /></div>
              <div style={{ fontSize: 11, color: 'var(--n400)' }}>
                {resume.nbVirements} virement{resume.nbVirements > 1 ? 's' : ''}
                {resume.nbSansVirement > 0 && ` · ${resume.nbSansVirement} sans virement (report)`}
              </div>
            </div>
            <div className="stat">
              <div className="label">Commission SwiftEats</div>
              <div className="val"><Compteur valeur={fcfa(resume.commission)} /></div>
            </div>
            <div className="stat">
              <div className="label">Retenues déduites</div>
              <div className="val"><Compteur valeur={fcfa(resume.retenues)} /></div>
            </div>
            {resume.fraisANotreCharge > 0 && (
              <div className="stat">
                <div className="label">Frais de virement (à notre charge)</div>
                <div className="val"><Compteur valeur={fcfa(resume.fraisANotreCharge)} /></div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div aria-live="polite">
        {erreur && (
          <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, margin: '0 0 12px' }}>
            {erreur}
          </p>
        )}
        {info && (
          <p role="status" style={{ color: 'var(--green)', fontSize: 13, margin: '0 0 12px' }}>
            {info}
          </p>
        )}
      </div>

      {aRevoir.length > 0 && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--danger)' }}>
          <div className="card-header">À revoir — solde négatif au-delà du plafond</div>
          <div className="card-body">
            <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--n700)' }}>
              Ces marchands doivent plus que le plafond toléré : leurs commandes ne sont plus incluses automatiquement. Examinez le
              dossier, puis relevez leur plafond ou incluez-les après vérification.
            </p>
            <table>
              <thead>
                <tr>
                  <th>Marchand</th>
                  <th>Dette</th>
                  <th>Plafond</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {aRevoir.map((s) => (
                  <tr key={s.marchandId}>
                    <td style={{ fontWeight: 600 }}>{s.nom}</td>
                    <td style={{ color: 'var(--danger)', fontWeight: 700 }}>{fcfa(s.dette)}</td>
                    <td>{fcfa(s.plafond)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        className="btn btn-outline btn-sm"
                        disabled={occupe}
                        onClick={() => {
                          const v = window.prompt(`Nouveau plafond de solde négatif pour ${s.nom} (FCFA) :`, String(s.plafond))
                          if (v === null) return
                          const p = Number(v.replace(/\s/g, ''))
                          if (!Number.isInteger(p) || p < 0) {
                            setErreur('Le plafond doit être un entier positif ou nul.')
                            return
                          }
                          void agir(() => fixerPlafondMarchand(s.marchandId, p), `Plafond de ${s.nom} : ${fcfa(p)}.`)
                        }}
                      >
                        Relever le plafond
                      </button>{' '}
                      <button
                        className="btn btn-outline btn-sm"
                        disabled={occupe}
                        onClick={() => {
                          if (!window.confirm(`Inclure ${s.nom} malgré sa dette de ${fcfa(s.dette)} ? Ses ventes serviront d’abord à la rembourser.`)) return
                          void agir(() => forcerInclusion(s.marchandId), `${s.nom} inclus après examen.`)
                        }}
                      >
                        Inclure après examen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">À virer ({enAttente.length})</div>
        <div className="card-body">
          <div className="search-bar" style={{ marginBottom: 12, alignItems: 'center' }}>
            <button className="btn btn-outline btn-sm" disabled={occupe} onClick={() => void agir(preparerReversements, 'Reversements actualisés.')}>
              Actualiser
            </button>
            <div className="spacer" style={{ flex: 1 }} />
            <button className="btn btn-green btn-sm" disabled={occupe || enAttente.length === 0} onClick={() => setToutOuvert(true)}>
              Marquer tout comme versé
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <caption className="sr-only">Reversements en attente, un par marchand et par jour</caption>
              <thead>
                <tr>
                  <th scope="col">Marchand</th>
                  <th scope="col">Jour</th>
                  <th scope="col">Cmd</th>
                  <th scope="col">Ventes</th>
                  <th scope="col">Commission</th>
                  <th scope="col">Retenues / report</th>
                  <th scope="col">Net à virer</th>
                  <th scope="col">Compte</th>
                  <th scope="col"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {enAttente.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', color: 'var(--n400)', padding: 20 }}>
                      Rien à virer pour le moment. Les commandes livrées apparaissent ici quelques minutes après la livraison.
                    </td>
                  </tr>
                )}
                {enAttente.map((l) => (
                  <Fragment key={l.id}>
                    <tr>
                      <td style={{ fontWeight: 600 }}>
                        {l.marchandNom}
                        {l.mode === 'historique' && <span className="badge badge-gray" style={{ marginLeft: 6 }}>ancien lot</span>}
                      </td>
                      <td>{jourLisible(l.jour)}</td>
                      <td>{l.nbCommandes}</td>
                      <td>{fcfa(l.ventes)}</td>
                      <td>−{fcfa(l.commission)}</td>
                      <td>
                        {l.retenues !== 0 || l.reportEntrant > 0 || l.reportSortant > 0 ? (
                          <>
                            {l.retenues !== 0 && <div>{l.retenues < 0 ? '−' : '+'}{fcfa(Math.abs(l.retenues))}</div>}
                            {l.reportEntrant > 0 && <div style={{ fontSize: 11, color: 'var(--n400)' }}>report −{fcfa(l.reportEntrant)}</div>}
                            {l.reportSortant > 0 && (
                              <div style={{ fontSize: 11, color: 'var(--danger)' }}>reste dû par le marchand : {fcfa(l.reportSortant)}</div>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ fontWeight: 700, color: l.net > 0 ? 'var(--green)' : 'var(--n400)' }}>
                        {l.net > 0 ? fcfa(l.net) : 'Rien à virer'}
                        {libelleFrais(l) && <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--n400)' }}>{libelleFrais(l)}</div>}
                      </td>
                      <td style={{ fontSize: 12 }}>{l.net > 0 ? compteAffiche(l.fournisseur, l.telephone) : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-outline btn-sm" aria-expanded={detail === l.id} onClick={() => void ouvrirReleve(l.id)}>
                          Relevé
                        </button>{' '}
                        <button className="btn btn-green btn-sm" disabled={occupe} onClick={() => setLotAConfirmer(l)}>
                          {l.net > 0 ? 'Confirmer le virement' : 'Clôturer'}
                        </button>
                      </td>
                    </tr>
                    {detail === l.id && (
                      <tr>
                        <td colSpan={9} style={{ background: 'var(--n50, #faf8f4)' }}>
                          <Releve donnees={releves[l.id]} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {resume.fraisANotreCharge > 0 && (
            <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--n700)' }}>
              Frais de virement (à notre charge) : {fcfa(resume.fraisANotreCharge)} sur {resume.nbVirements} virement
              {resume.nbVirements > 1 ? 's' : ''}. Ils ne sont jamais déduits du marchand : il reçoit exactement son net.
            </p>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">Soldes et retenues</div>
        <div className="card-body">
          <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--n700)' }}>
            Un remboursement client ou un litige après un reversement est déduit du reversement suivant, et reporté s’il le dépasse.
            Le solde est toujours calculé à partir des lignes — jamais saisi.
          </p>
          <FormulaireAjustement soldes={soldes} lots={lots} occupe={occupe} surEnvoi={(m, t, v, motif) => agir(() => enregistrerAjustement(m, t, v, motif), 'Enregistré : il sera appliqué au prochain reversement.')} />
          {soldes.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table>
                <caption className="sr-only">Solde de chaque marchand</caption>
                <thead>
                  <tr>
                    <th scope="col">Marchand</th>
                    <th scope="col">À verser</th>
                    <th scope="col">Dette</th>
                    <th scope="col">Retenues en attente</th>
                    <th scope="col">Plafond</th>
                    <th scope="col">État</th>
                  </tr>
                </thead>
                <tbody>
                  {soldes.map((s) => (
                    <tr key={s.marchandId}>
                      <td style={{ fontWeight: 600 }}>{s.nom}</td>
                      <td>{fcfa(s.aVerser)}</td>
                      <td style={{ color: s.dette > 0 ? 'var(--danger)' : undefined }}>{s.dette > 0 ? fcfa(s.dette) : '—'}</td>
                      <td>{s.ajustementsEnAttente !== 0 ? `${s.ajustementsEnAttente < 0 ? '−' : '+'}${fcfa(Math.abs(s.ajustementsEnAttente))}` : '—'}</td>
                      <td>{fcfa(s.plafond)}</td>
                      <td>
                        <span className={`badge ${s.aRevoir ? 'badge-red' : s.dette > 0 ? 'badge-yellow' : 'badge-green'}`}>
                          {s.aRevoir ? 'À revoir' : s.dette > 0 ? 'Solde négatif toléré' : 'En règle'}
                        </span>
                        {!s.compteVerifie && s.aVerser > 0 && <span className="badge badge-gray" style={{ marginLeft: 6 }}>compte non vérifié</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">Historique des reversements versés</div>
        <div className="card-body" style={{ overflowX: 'auto' }}>
          <table>
            <caption className="sr-only">Derniers reversements versés</caption>
            <thead>
              <tr>
                <th scope="col">Versé le</th>
                <th scope="col">Marchand</th>
                <th scope="col">Jour</th>
                <th scope="col">Cmd</th>
                <th scope="col">Net versé</th>
                <th scope="col">Référence</th>
                <th scope="col"><span className="sr-only">Relevé</span></th>
              </tr>
            </thead>
            <tbody>
              {verses.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--n400)', padding: 18 }}>
                    Aucun reversement versé pour le moment.
                  </td>
                </tr>
              )}
              {verses.map((l) => (
                <Fragment key={l.id}>
                  <tr>
                    <td>{heureLisible(l.payeLe)}</td>
                    <td style={{ fontWeight: 600 }}>{l.marchandNom}</td>
                    <td>{jourLisible(l.jour)}</td>
                    <td>{l.nbCommandes}</td>
                    <td style={{ fontWeight: 700 }}>{l.net > 0 ? fcfa(l.net) : 'Aucun virement'}</td>
                    <td style={{ fontSize: 12 }}>{l.reference ?? '—'}</td>
                    <td>
                      <button className="btn btn-outline btn-sm" aria-expanded={detail === l.id} onClick={() => void ouvrirReleve(l.id)}>
                        Relevé
                      </button>
                    </td>
                  </tr>
                  {detail === l.id && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--n50, #faf8f4)' }}>
                        <Releve donnees={releves[l.id]} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {reglages && <CarteReglages reglages={reglages} modifiable={estAdmin} occupe={occupe} surEnregistrer={(r) => agir(() => ecrireReglages(r), 'Réglages enregistrés.')} />}

      <ConfirmerLot lot={lotAConfirmer} occupe={occupe} surFermer={() => setLotAConfirmer(null)} surConfirmer={async (ref, fournisseur, tel) => {
        const lot = lotAConfirmer
        if (!lot) return
        setLotAConfirmer(null)
        await agir(() => marquerLotVerse(lot, ref, fournisseur, tel), `${lot.marchandNom} : reversement marqué versé.`)
      }} />

      <ConfirmerTout lots={enAttente} ouverte={toutOuvert} occupe={occupe} surFermer={() => setToutOuvert(false)} surConfirmer={async (ref) => {
        setToutOuvert(false)
        await agir(async () => {
          const r = await marquerToutVerse(enAttente, ref)
          setInfo(`${r.marques} reversement${r.marques > 1 ? 's' : ''} marqué${r.marques > 1 ? 's' : ''} versé${r.marques > 1 ? 's' : ''}${r.dejaVerses > 0 ? ` (${r.dejaVerses} déjà fait${r.dejaVerses > 1 ? 's' : ''} par un collègue)` : ''}.`)
        })
      }} />
    </>
  )
}

function Releve({ donnees }: { donnees: LigneReleve[] | 'chargement' | 'erreur' | undefined }) {
  if (donnees === undefined || donnees === 'chargement') return <p style={{ margin: 8, fontSize: 12 }}>Chargement du relevé…</p>
  if (donnees === 'erreur') return <p style={{ margin: 8, fontSize: 12, color: 'var(--danger)' }}>Relevé indisponible.</p>
  if (donnees.length === 0) return <p style={{ margin: 8, fontSize: 12 }}>Aucune ligne.</p>
  return (
    <table style={{ margin: '6px 0' }}>
      <thead>
        <tr>
          <th scope="col">Ligne</th>
          <th scope="col">Livrée</th>
          <th scope="col">Ventes</th>
          <th scope="col">Commission</th>
          <th scope="col">Montant</th>
        </tr>
      </thead>
      <tbody>
        {donnees.map((d, i) => (
          <tr key={i}>
            <td>{d.genre === 'commande' ? d.reference : `Retenue / crédit — ${d.motif ?? ''}`}</td>
            <td>{d.livreeLe ? heureLisible(d.livreeLe) : '—'}</td>
            <td>{d.ventes !== null ? fcfa(d.ventes) : '—'}</td>
            <td>{d.commission !== null ? `−${fcfa(d.commission)}` : '—'}</td>
            <td style={{ fontWeight: 600, color: d.montant < 0 ? 'var(--danger)' : undefined }}>{d.montant < 0 ? '−' : ''}{fcfa(Math.abs(d.montant))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ConfirmerLot({
  lot,
  occupe,
  surFermer,
  surConfirmer,
}: {
  lot: LotReversement | null
  occupe: boolean
  surFermer: () => void
  surConfirmer: (reference: string, fournisseur: string | null, telephone: string | null) => Promise<void>
}) {
  const [reference, setReference] = useState('')
  const [fournisseur, setFournisseur] = useState('')
  const [telephone, setTelephone] = useState('')
  useEffect(() => {
    setReference('')
    setFournisseur(lot?.fournisseur ?? '')
    setTelephone(lot?.telephone ?? '')
  }, [lot?.id, lot?.fournisseur, lot?.telephone])
  const exigeRef = (lot?.net ?? 0) > 0
  const pret = !exigeRef || reference.trim().length > 0
  return (
    <Modale titre={lot ? `Confirmer le virement — ${lot.marchandNom}` : 'Confirmer'} ouverte={lot !== null} surFermer={surFermer}>
      {lot && (
        <>
          <p style={{ fontSize: 13, margin: '0 0 8px' }}>
            {lot.net > 0 ? (
              <>Montant à virer : <b>{fcfa(lot.net)}</b> ({lot.nbCommandes} commande{lot.nbCommandes > 1 ? 's' : ''} du {jourLisible(lot.jour)}).</>
            ) : (
              <>Ce reversement ne comporte aucun virement : les retenues ont absorbé les ventes du jour. Le clôturer ne déplace pas d’argent.</>
            )}
          </p>
          {libelleFrais(lot) && <p style={{ fontSize: 12, margin: '0 0 8px', color: 'var(--n700)' }}>{libelleFrais(lot)}</p>}
          {exigeRef && (
            <>
              <label htmlFor="rev-ref">Référence du virement (numéro de transaction)</label>
              <input id="rev-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="ex. MP260514.2130.A12345" autoComplete="off" />
              <label htmlFor="rev-op">Opérateur utilisé</label>
              <select id="rev-op" value={fournisseur} onChange={(e) => setFournisseur(e.target.value)}>
                <option value="orange_money">Orange Money</option>
                <option value="wave">Wave</option>
                {!['orange_money', 'wave'].includes(fournisseur) && <option value="">—</option>}
              </select>
              <label htmlFor="rev-tel">Numéro utilisé pour le virement</label>
              <input id="rev-tel" value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="+223 …" inputMode="tel" autoComplete="off" />
            </>
          )}
          <p style={{ fontSize: 12, color: 'var(--n700)', margin: '10px 0' }}>
            Ne confirmez qu’après avoir fait le virement : cela enregistre qu’il a été effectué et affiche « Reversé » côté marchand.
          </p>
          <button className="btn btn-green" disabled={occupe || !pret} onClick={() => void surConfirmer(reference, fournisseur || null, telephone || null)}>
            {exigeRef ? 'Le virement est fait' : 'Clôturer sans virement'}
          </button>
        </>
      )}
    </Modale>
  )
}

function ConfirmerTout({
  lots,
  ouverte,
  occupe,
  surFermer,
  surConfirmer,
}: {
  lots: LotReversement[]
  ouverte: boolean
  occupe: boolean
  surFermer: () => void
  surConfirmer: (reference: string) => Promise<void>
}) {
  const [reference, setReference] = useState('')
  const [coche, setCoche] = useState(false)
  useEffect(() => {
    if (ouverte) {
      setReference('')
      setCoche(false)
    }
  }, [ouverte])
  const r = resumeEnAttente(lots)
  return (
    <Modale titre="Marquer tout comme versé" ouverte={ouverte} surFermer={surFermer}>
      <p style={{ fontSize: 13, margin: '0 0 8px' }}>
        {r.nbVirements} virement{r.nbVirements > 1 ? 's' : ''} pour <b>{fcfa(r.aVirer)}</b>
        {r.nbSansVirement > 0 && <> et {r.nbSansVirement} reversement{r.nbSansVirement > 1 ? 's' : ''} sans virement (clôturé{r.nbSansVirement > 1 ? 's' : ''})</>}.
      </p>
      <label htmlFor="tout-ref">Référence commune (facultative — elle est notée sur chaque reversement)</label>
      <input id="tout-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="ex. LOT du soir" autoComplete="off" />
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12 }}>
        <input type="checkbox" checked={coche} onChange={(e) => setCoche(e.target.checked)} style={{ marginTop: 3 }} />
        <span>J’ai effectué tous ces virements (le serveur refusera si un montant a changé depuis l’affichage).</span>
      </label>
      <button className="btn btn-green" style={{ marginTop: 12 }} disabled={occupe || !coche || lots.length === 0} onClick={() => void surConfirmer(reference)}>
        Marquer tout comme versé
      </button>
    </Modale>
  )
}

function FormulaireAjustement({
  soldes,
  lots,
  occupe,
  surEnvoi,
}: {
  soldes: SoldeMarchand[]
  lots: LotReversement[]
  occupe: boolean
  surEnvoi: (marchandId: string, type: 'retenue' | 'credit', montant: number, motif: string) => Promise<void>
}) {
  const marchands = useMemo(() => {
    const m = new Map<string, string>()
    for (const s of soldes) m.set(s.marchandId, s.nom)
    for (const l of lots) m.set(l.marchandId, l.marchandNom)
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1], 'fr'))
  }, [soldes, lots])
  const [marchand, setMarchand] = useState('')
  const [type, setType] = useState<'retenue' | 'credit'>('retenue')
  const [montant, setMontant] = useState('')
  const [motif, setMotif] = useState('')
  const m = Number(montant.replace(/\s/g, ''))
  const valide = marchand !== '' && Number.isInteger(m) && m > 0 && motif.trim() !== ''
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!valide) return
        void surEnvoi(marchand, type, m, motif).then(() => {
          setMontant('')
          setMotif('')
        })
      }}
      style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, alignItems: 'end' }}
    >
      <div>
        <label htmlFor="aj-m" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Marchand</label>
        <select id="aj-m" value={marchand} onChange={(e) => setMarchand(e.target.value)} style={{ width: '100%' }}>
          <option value="">Choisir…</option>
          {marchands.map(([id, nom]) => (
            <option key={id} value={id}>{nom}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="aj-t" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Nature</label>
        <select id="aj-t" value={type} onChange={(e) => setType(e.target.value as 'retenue' | 'credit')} style={{ width: '100%' }}>
          <option value="retenue">Retenue (à déduire)</option>
          <option value="credit">Crédit (en sa faveur)</option>
        </select>
      </div>
      <div>
        <label htmlFor="aj-v" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Montant (F)</label>
        <input id="aj-v" value={montant} onChange={(e) => setMontant(e.target.value)} inputMode="numeric" style={{ width: '100%' }} />
      </div>
      <div style={{ gridColumn: 'span 2' }}>
        <label htmlFor="aj-o" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Motif (visible du marchand)</label>
        <input id="aj-o" value={motif} onChange={(e) => setMotif(e.target.value)} style={{ width: '100%' }} />
      </div>
      <button className="btn btn-outline btn-sm" type="submit" disabled={occupe || !valide}>
        Enregistrer
      </button>
    </form>
  )
}

function CarteReglages({
  reglages,
  modifiable,
  occupe,
  surEnregistrer,
}: {
  reglages: ReglagesReversement
  modifiable: boolean
  occupe: boolean
  surEnregistrer: (r: ReglagesReversement) => Promise<void>
}) {
  const [brouillon, setBrouillon] = useState(reglages)
  useEffect(() => setBrouillon(reglages), [reglages])
  const erreurs = validerReglages(brouillon)
  const champ = <K extends keyof ReglagesReversement>(k: K, v: ReglagesReversement[K]) => setBrouillon((b) => ({ ...b, [k]: v }))
  const num = (s: string) => (s.trim() === '' ? NaN : Number(s))
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">Réglages des reversements{modifiable ? '' : ' (lecture seule : réservé aux administrateurs)'}</div>
      <div className="card-body">
        <fieldset disabled={!modifiable} style={{ border: 0, padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
          <div>
            <label htmlFor="rg-mode" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Mode</label>
            <select id="rg-mode" value={brouillon.payoutMode} onChange={(e) => champ('payoutMode', e.target.value as ReglagesReversement['payoutMode'])} style={{ width: '100%' }}>
              <option value="quotidien">Un reversement par marchand et par jour</option>
              <option value="par_commande">Un reversement par commande (API Orange / Wave)</option>
            </select>
          </div>
          <div>
            <label htmlFor="rg-h" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Fin de la journée (heure limite)</label>
            <input id="rg-h" value={brouillon.heureLimite} onChange={(e) => champ('heureLimite', e.target.value)} placeholder="23:59" style={{ width: '100%' }} />
          </div>
          <div>
            <label htmlFor="rg-fz" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Fuseau horaire</label>
            <input id="rg-fz" value={brouillon.fuseau} onChange={(e) => champ('fuseau', e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <label htmlFor="rg-d" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Confirmation automatique (minutes après la livraison)</label>
            <input id="rg-d" value={Number.isNaN(brouillon.delaiConfirmationMin) ? '' : brouillon.delaiConfirmationMin} onChange={(e) => champ('delaiConfirmationMin', num(e.target.value))} inputMode="numeric" style={{ width: '100%' }} />
          </div>
          <div>
            <label htmlFor="rg-p" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Plafond de solde négatif (F)</label>
            <input id="rg-p" value={Number.isNaN(brouillon.plafondNegatif) ? '' : brouillon.plafondNegatif} onChange={(e) => champ('plafondNegatif', num(e.target.value))} inputMode="numeric" style={{ width: '100%' }} />
          </div>
          <div>
            <label htmlFor="rg-s" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Première journée gérée</label>
            <input id="rg-s" type="date" value={brouillon.debut} onChange={(e) => champ('debut', e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <label htmlFor="rg-f" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Frais de virement par versement (F, 0 = inconnu)</label>
            <input id="rg-f" value={Number.isNaN(brouillon.fraisVirement) ? '' : brouillon.fraisVirement} onChange={(e) => champ('fraisVirement', num(e.target.value))} inputMode="numeric" style={{ width: '100%' }} />
          </div>
          <div>
            <label htmlFor="rg-c" style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Frais de virement : à la charge de</label>
            <select id="rg-c" value={brouillon.fraisACharge} onChange={(e) => champ('fraisACharge', e.target.value as ReglagesReversement['fraisACharge'])} style={{ width: '100%' }}>
              <option value="plateforme">SwiftEats (inclus dans notre commission)</option>
              <option value="marchand">Le marchand</option>
              <option value="partage">Partagés</option>
            </select>
          </div>
        </fieldset>
        {brouillon.fraisACharge !== 'plateforme' && (
          <p role="note" style={{ fontSize: 12, color: 'var(--danger)', margin: '10px 0 0' }}>
            Attention : la décision en vigueur est que les frais de virement sont inclus dans notre commission (le marchand reçoit exactement son net).
          </p>
        )}
        {modifiable && (
          <div style={{ marginTop: 12 }}>
            {erreurs.length > 0 && <p role="alert" style={{ color: 'var(--danger)', fontSize: 12, margin: '0 0 8px' }}>{erreurs[0]}</p>}
            <button className="btn btn-outline btn-sm" disabled={occupe || erreurs.length > 0 || JSON.stringify(brouillon) === JSON.stringify(reglages)} onClick={() => void surEnregistrer(brouillon)}>
              Enregistrer les réglages
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
